import { ChargeId } from "shared/hubrise/ids"
import { ICatalog, ICharge } from "shared/hubrise/models"
import {
  findById,
  insertItemInArray,
  moveItemWithinArray,
  removeInArrayById,
  replaceInArrayById,
} from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"

/**
 * Add a charge.
 */
export function add(
  catalog: ICatalog,
  insertAfter: ICharge | undefined,
  values: Partial<ICharge>,
): { newCatalog: ICatalog; charge: ICharge } {
  const position: number = insertAfter
    ? catalog.data.charges.findIndex((charge) => charge.id === insertAfter.id) + 1
    : 0
  const id = ChargeId(catalog.idGenerator())
  const defaultValues: ICharge = {
    // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
    id,
    ref: "",
    name: "",
    type: "delivery" as const,
    restrictions: { enabled: true },
    price: null,
  }
  const charge: ICharge = { ...defaultValues, ...values }
  const charges: Array<ICharge> = insertItemInArray(catalog.data.charges, charge, position)
  const newCatalog: ICatalog = updatedCatalog(catalog, { charges })
  return { newCatalog, charge }
}

/**
 * Delete the charge. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param chargeId
 */
export function doDelete(catalog: ICatalog, chargeId: ChargeId): ICatalog {
  return updatedCatalog(catalog, { charges: removeInArrayById(catalog.data.charges, chargeId) })
}

/**
 * Return the charge matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: ChargeId): ICharge => {
  const charge = findById(catalog.data.charges, id)
  if (!charge) throw `Charge ${id} not found`
  return charge
}

/**
 * Move a charge to a new position.
 * @param catalog
 * @param charge
 * @param newPosition
 */
export function move(catalog: ICatalog, charge: ICharge, newPosition: number): ICatalog {
  const charges: Array<ICharge> = moveItemWithinArray(catalog.data.charges, charge, newPosition)
  return updatedCatalog(catalog, { charges })
}

/**
 * Update a charge and return the resulting catalog.
 * @param catalog
 * @param id
 * @param fieldName
 * @param value
 */
export function update<T extends keyof ICharge>(
  catalog: ICatalog,
  id: ChargeId,
  fieldName: T,
  value: ICharge[T],
): ICatalog {
  return updatedCatalog(catalog, {
    charges: replaceInArrayById(catalog.data.charges, id, (charge) => ({
      ...charge,
      [fieldName]: value,
    })),
  })
}
